import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button, Input, Select } from '../atoms';
import { StatCard, useToast } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import { useIsCompact } from '../lib/useMediaQuery';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * ParcelValuationTab — the Valuation tab body on ParcelDetail (/v2/parcels/:id).
 *
 * Wires the REAL Domain-12 valuation router (src/routes/valuation.js):
 *   - GET  /api/valuation/inputs?parcel_id=            load the saved input set
 *   - POST /api/valuation/parcels/:parcelId/compute    live compute preview (no persist)
 *   - POST /api/valuation/inputs                        create + persist an input set
 *   - PUT  /api/valuation/inputs/:id                    update + recompute an input set
 *   - POST /api/valuation/packets                       kick off branded-packet render → 202
 *   - GET  /api/valuation/packets?parcel_id=            list packets
 *   - GET  /api/valuation/packets/:id                   poll a packet envelope
 *   - GET  /api/valuation/packets/:id/download          download the final PDF
 *
 * Money-redaction is respected transparently: the money-blind role receives
 * null money fields from the guarded res.json and they render as "—". The
 * packet-download boundary is enforced server-side (403 for that role) and
 * surfaced here as a toast rather than being softened.
 */

interface LineItemForm {
  key: string;
  kind: 'income' | 'expense';
  label: string;
  amount: string;
}

interface ValuationLineItem {
  id: string;
  kind: 'income' | 'expense';
  label: string;
  amount: number | null;
  sort_order: number;
}

interface ValuationInput {
  id: string;
  parcel_id: string;
  tax_year: number;
  methodology: string;
  potential_gross_income: number | null;
  vacancy_rate: number | null;
  effective_gross_income: number | null;
  total_operating_expenses: number | null;
  noi: number | null;
  base_cap_rate: number | null;
  cap_rate_adjustment: number | null;
  effective_cap_rate: number | null;
  indicated_value: number | null;
  notes: string | null;
  line_items: ValuationLineItem[];
}

interface CrossCheck {
  reference: number;
  difference: number;
  ratio: number;
}

interface ComputeResult {
  parcel_id?: string;
  tax_year?: number | null;
  pgi: number;
  vacancy_rate: number;
  vacancy: number;
  egi: number;
  opex: number;
  noi: number;
  base_cap_rate: number;
  cap_rate_adjustment: number;
  effective_cap_rate: number;
  indicated_value: number;
  value_per_sf: number | null;
  value_per_unit: number | null;
  cross_check: {
    vs_current_assessed: CrossCheck | null;
    vs_proposed: CrossCheck | null;
  };
}

interface PacketEnvelope {
  packet_id: string;
  status: 'generating' | 'final' | 'failed' | string;
  parcel_id: string;
  tax_year: number;
  indicated_value: number | null;
  effective_cap_rate: number | null;
  noi: number | null;
  document_id: string | null;
  download_available: boolean;
  error: string | null;
  generated_at: string | null;
}

const METHODOLOGY = 'income';

function money(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function rate(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toLocaleString(undefined, { maximumFractionDigits: 3 })}%`;
}

function numOrUndef(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return err instanceof Error ? err.message : 'Request failed';
}

let liSeq = 0;
function newLineItem(kind: 'income' | 'expense'): LineItemForm {
  liSeq += 1;
  return { key: `li-${Date.now()}-${liSeq}`, kind, label: '', amount: '' };
}

export function ParcelValuationTab({ parcelId, taxYear }: { parcelId: string; taxYear: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const canView = useEntityPermission('valuation', 'can_view');
  const canAdd = useEntityPermission('valuation', 'can_add');
  const canEdit = useEntityPermission('valuation', 'can_edit');

  // Form state
  const [pgi, setPgi] = useState('');
  const [vacancyRate, setVacancyRate] = useState('');
  const [baseCapRate, setBaseCapRate] = useState('');
  const [capRateAdjustment, setCapRateAdjustment] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemForm[]>([]);
  const [preview, setPreview] = useState<ComputeResult | null>(null);
  const [activePacketId, setActivePacketId] = useState<string | null>(null);
  const [hydratedId, setHydratedId] = useState<string | null>(null);

  // ── Load the saved input set for this parcel/tax_year (real data) ──
  const inputsQuery = useQuery<ValuationInput[]>({
    queryKey: ['valuation-inputs', parcelId],
    queryFn: ({ signal }) => api.get<ValuationInput[]>(`/api/valuation/inputs?parcel_id=${parcelId}`, signal),
    enabled: !!parcelId && canView,
  });

  const existing = useMemo(
    () => (inputsQuery.data ?? []).find((i) => i.tax_year === taxYear && i.methodology === METHODOLOGY) ?? null,
    [inputsQuery.data, taxYear],
  );

  // Hydrate the form once from the loaded input set (re-hydrate if the row id changes).
  useEffect(() => {
    if (existing && existing.id !== hydratedId) {
      setPgi(existing.potential_gross_income != null ? String(existing.potential_gross_income) : '');
      setVacancyRate(existing.vacancy_rate != null ? String(existing.vacancy_rate) : '');
      setBaseCapRate(existing.base_cap_rate != null ? String(existing.base_cap_rate) : '');
      setCapRateAdjustment(existing.cap_rate_adjustment != null ? String(existing.cap_rate_adjustment) : '');
      setNotes(existing.notes ?? '');
      setLineItems(
        (existing.line_items ?? []).map((li) => {
          liSeq += 1;
          return { key: `li-${li.id}-${liSeq}`, kind: li.kind, label: li.label ?? '', amount: li.amount != null ? String(li.amount) : '' };
        }),
      );
      setHydratedId(existing.id);
    }
  }, [existing, hydratedId]);

  // ── Packets list (real data) ──
  const packetsQuery = useQuery<PacketEnvelope[]>({
    queryKey: ['valuation-packets', parcelId],
    queryFn: ({ signal }) => api.get<PacketEnvelope[]>(`/api/valuation/packets?parcel_id=${parcelId}`, signal),
    enabled: !!parcelId && canView,
  });

  // ── Poll the in-flight packet until it settles (real polling of the real row) ──
  const packetPollQuery = useQuery<PacketEnvelope>({
    queryKey: ['valuation-packet', activePacketId],
    queryFn: ({ signal }) => api.get<PacketEnvelope>(`/api/valuation/packets/${activePacketId}`, signal),
    enabled: !!activePacketId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s && s !== 'generating' ? false : 1500;
    },
  });

  // When the polled packet settles, refresh the list + surface the result.
  useEffect(() => {
    const p = packetPollQuery.data;
    if (!p || !activePacketId) return;
    if (p.status === 'final') {
      toast({ variant: 'success', message: 'Appeal packet ready.' });
      queryClient.invalidateQueries({ queryKey: ['valuation-packets', parcelId] });
      setActivePacketId(null);
    } else if (p.status === 'failed') {
      toast({ variant: 'error', message: p.error || 'Packet generation failed.' });
      queryClient.invalidateQueries({ queryKey: ['valuation-packets', parcelId] });
      setActivePacketId(null);
    }
  }, [packetPollQuery.data, activePacketId, parcelId, queryClient, toast]);

  function buildFields() {
    return {
      pgi: numOrUndef(pgi),
      vacancy_rate: numOrUndef(vacancyRate),
      base_cap_rate: numOrUndef(baseCapRate),
      cap_rate_adjustment: numOrUndef(capRateAdjustment),
      line_items: lineItems
        .filter((li) => li.label.trim() !== '' || li.amount.trim() !== '')
        .map((li, idx) => ({ kind: li.kind, label: li.label || li.kind, amount: numOrUndef(li.amount) ?? 0, sort_order: idx })),
    };
  }

  // ── Live compute preview (POST /compute, no persist) ──
  const computeMutation = useMutation<ComputeResult, Error>({
    mutationFn: () => api.post<ComputeResult>(`/api/valuation/parcels/${parcelId}/compute`, { ...buildFields(), tax_year: taxYear }),
    onSuccess: (data) => setPreview(data),
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  // ── Save (create or update the input set) ──
  const saveMutation = useMutation<ValuationInput & { computed?: ComputeResult }, Error>({
    mutationFn: () => {
      const body = { parcel_id: parcelId, tax_year: taxYear, methodology: METHODOLOGY, notes: notes || null, ...buildFields() };
      return existing
        ? api.put<ValuationInput & { computed?: ComputeResult }>(`/api/valuation/inputs/${existing.id}`, body)
        : api.post<ValuationInput & { computed?: ComputeResult }>('/api/valuation/inputs', body);
    },
    onSuccess: (data) => {
      toast({ variant: 'success', message: existing ? 'Valuation updated.' : 'Valuation saved.' });
      if (data.computed) setPreview(data.computed);
      setHydratedId(null); // allow re-hydrate from the refreshed row
      queryClient.invalidateQueries({ queryKey: ['valuation-inputs', parcelId] });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  // ── Generate branded packet (POST /packets → 202, then poll) ──
  const generateMutation = useMutation<PacketEnvelope, Error>({
    mutationFn: () => {
      if (!existing) throw new Error('Save the valuation before generating a packet.');
      return api.post<PacketEnvelope>('/api/valuation/packets', {
        parcel_id: parcelId,
        tax_year: taxYear,
        valuation_input_id: existing.id,
      });
    },
    onSuccess: (env) => {
      toast({ variant: 'success', message: 'Packet generation started.' });
      setActivePacketId(env.packet_id);
      queryClient.invalidateQueries({ queryKey: ['valuation-packets', parcelId] });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  // Two side-by-side editor/output columns don't fit a phone; below the desktop
  // breakpoint they stack so the fixed-width line-item rows can't force overflow.
  const isCompact = useIsCompact();

  if (!canView) {
    return <Notice>You do not have permission to view valuations for this parcel.</Notice>;
  }

  const busyPacket = !!activePacketId || generateMutation.isPending;
  const packets = packetsQuery.data ?? [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--sp-4)', alignItems: 'start' }}>
      {/* ── Left: input editor ── */}
      <Card label={`Income-approach inputs · Tax year ${taxYear}`}>
        <FieldRow label="Potential gross income (PGI)">
          <Input type="number" value={pgi} onChange={(e) => setPgi(e.target.value)} placeholder="e.g. 500000" disabled={!canAdd && !canEdit} />
        </FieldRow>
        <FieldRow label="Vacancy rate (fraction, e.g. 0.05 = 5%)">
          <Input type="number" step="0.001" value={vacancyRate} onChange={(e) => setVacancyRate(e.target.value)} placeholder="0.05" disabled={!canAdd && !canEdit} />
        </FieldRow>
        <FieldRow label="Base cap rate (fraction, e.g. 0.08 = 8%)">
          <Input type="number" step="0.001" value={baseCapRate} onChange={(e) => setBaseCapRate(e.target.value)} placeholder="0.08" disabled={!canAdd && !canEdit} />
        </FieldRow>
        <FieldRow label="Cap-rate adjustment (fraction, +/-)">
          <Input type="number" step="0.001" value={capRateAdjustment} onChange={(e) => setCapRateAdjustment(e.target.value)} placeholder="0.005" disabled={!canAdd && !canEdit} />
        </FieldRow>

        <div style={{ marginTop: 'var(--sp-4)' }}>
          <SubHead>Line items</SubHead>
          {lineItems.length === 0 && (
            <p style={{ margin: '0 0 var(--sp-2)', color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
              No line items. Expenses reduce NOI; income lines derive PGI when the scalar PGI above is blank.
            </p>
          )}
          {lineItems.map((li, idx) => (
            <div key={li.key} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 120px 32px', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)', alignItems: 'center' }}>
              <Select
                options={[{ value: 'income', label: 'Income' }, { value: 'expense', label: 'Expense' }]}
                value={li.kind}
                size="sm"
                onChange={(e) => setLineItems((prev) => prev.map((x, i) => (i === idx ? { ...x, kind: e.target.value as 'income' | 'expense' } : x)))}
                disabled={!canAdd && !canEdit}
              />
              <Input size="sm" value={li.label} placeholder="Label" onChange={(e) => setLineItems((prev) => prev.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))} disabled={!canAdd && !canEdit} />
              <Input size="sm" type="number" value={li.amount} placeholder="Amount" onChange={(e) => setLineItems((prev) => prev.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)))} disabled={!canAdd && !canEdit} />
              <Button variant="ghost" size="sm" onClick={() => setLineItems((prev) => prev.filter((_, i) => i !== idx))} disabled={!canAdd && !canEdit} aria-label="Remove line item">✕</Button>
            </div>
          ))}
          {(canAdd || canEdit) && (
            <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
              <Button variant="secondary" size="sm" onClick={() => setLineItems((prev) => [...prev, newLineItem('income')])}>+ Income</Button>
              <Button variant="secondary" size="sm" onClick={() => setLineItems((prev) => [...prev, newLineItem('expense')])}>+ Expense</Button>
            </div>
          )}
        </div>

        <div style={{ marginTop: 'var(--sp-4)' }}>
          <SubHead>Notes</SubHead>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canAdd && !canEdit}
            rows={2}
            style={{
              width: '100%', fontFamily: 'inherit', fontSize: 'var(--fs-sm)', padding: '8px 12px',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', background: 'var(--color-card)',
              color: 'var(--color-ink)', outline: 'none', resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-4)', flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" loading={computeMutation.isPending} onClick={() => computeMutation.mutate()}>
            Compute preview
          </Button>
          {(canAdd || canEdit) && (
            <Button
              variant="primary"
              size="sm"
              loading={saveMutation.isPending}
              disabled={existing ? !canEdit : !canAdd}
              onClick={() => saveMutation.mutate()}
            >
              {existing ? 'Update valuation' : 'Save valuation'}
            </Button>
          )}
        </div>
      </Card>

      {/* ── Right: compute preview + packets ── */}
      <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        <Card label="Compute preview">
          {preview ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
                <StatCard label="Indicated value" value={money(preview.indicated_value)} caption={`Effective cap ${rate(preview.effective_cap_rate)}`} accent="hunter" />
                <StatCard label="NOI" value={money(preview.noi)} caption={`EGI ${money(preview.egi)}`} accent="green" />
              </div>
              <WaterRow label="PGI (potential gross income)" value={money(preview.pgi)} />
              <WaterRow label={`Vacancy & collection loss (${rate(preview.vacancy_rate)})`} value={`(${money(preview.vacancy)})`} />
              <WaterRow label="EGI (effective gross income)" value={money(preview.egi)} strong />
              <WaterRow label="Operating expenses" value={`(${money(preview.opex)})`} />
              <WaterRow label="NOI (net operating income)" value={money(preview.noi)} strong />
              <WaterRow label="Effective cap rate" value={rate(preview.effective_cap_rate)} />
              <WaterRow label="Indicated value" value={money(preview.indicated_value)} strong />
              {(preview.value_per_sf != null || preview.value_per_unit != null) && (
                <div style={{ display: 'flex', gap: 'var(--sp-4)', marginTop: 'var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                  {preview.value_per_sf != null && <span>Value / SF: {money(preview.value_per_sf)}</span>}
                  {preview.value_per_unit != null && <span>Value / unit: {money(preview.value_per_unit)}</span>}
                </div>
              )}
              {(preview.cross_check?.vs_current_assessed || preview.cross_check?.vs_proposed) && (
                <div style={{ marginTop: 'var(--sp-3)' }}>
                  <SubHead>Cross-check</SubHead>
                  {preview.cross_check.vs_current_assessed && (
                    <WaterRow label="vs current assessed" value={`${money(preview.cross_check.vs_current_assessed.reference)} · ${(preview.cross_check.vs_current_assessed.ratio * 100).toFixed(1)}%`} />
                  )}
                  {preview.cross_check.vs_proposed && (
                    <WaterRow label="vs proposed" value={`${money(preview.cross_check.vs_proposed.reference)} · ${(preview.cross_check.vs_proposed.ratio * 100).toFixed(1)}%`} />
                  )}
                </div>
              )}
            </>
          ) : (
            <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
              Enter inputs and press <strong>Compute preview</strong> to see the income-approach waterfall.
            </p>
          )}
        </Card>

        <Card label="Appeal packets">
          <div style={{ marginBottom: 'var(--sp-3)' }}>
            <Button
              variant="primary"
              size="sm"
              loading={busyPacket}
              disabled={!existing || !canAdd}
              onClick={() => generateMutation.mutate()}
            >
              {busyPacket ? 'Generating…' : 'Generate packet'}
            </Button>
            {!existing && (
              <p style={{ margin: 'var(--sp-2) 0 0', color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
                Save the valuation first to generate a branded packet.
              </p>
            )}
          </div>
          {packets.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No packets generated yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              {packets.map((p) => (
                <div key={p.packet_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', padding: 'var(--sp-2) var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
                  <div style={{ fontSize: 'var(--fs-sm)' }}>
                    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                      <PacketStatus status={p.status} />
                      <span style={{ color: 'var(--color-ink-3)' }}>{money(p.indicated_value)}</span>
                    </div>
                    <div style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-xs)', marginTop: '2px' }}>
                      {p.generated_at ? formatDateTime(p.generated_at) : p.error || 'Pending…'}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!p.download_available}
                    onClick={() => downloadDocument(p.packet_id, (m) => toast({ variant: 'error', message: m }), `/api/valuation/packets/${p.packet_id}/download`)}
                  >
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function PacketStatus({ status }: { status: string }) {
  const variant = status === 'final' ? 'success' : status === 'failed' ? 'danger' : 'warning';
  const label = status === 'final' ? 'Ready' : status === 'failed' ? 'Failed' : 'Generating';
  return <Badge variant={variant} dot>{label}</Badge>;
}

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{label}</h3>
      {children}
    </div>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wide)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>{children}</div>;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-1)' }}>{label}</label>
      {children}
    </div>
  );
}

function WaterRow({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: strong ? 'var(--fw-semibold)' : 'var(--fw-medium)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)' }}>{children}</div>
  );
}
